/**
 * Auth Context
 * Manages authentication state and user profile from custom backend
 */

import React, { createContext, useState, useCallback, useEffect, useRef } from 'react'
import { authService } from '../services/auth.service'
import { userService } from '../services/user.service'
import { User, UserRole } from '../types/user.types'
import { STORAGE_KEYS } from '../types/route.types'
import { getAccessToken, clearAccessToken } from '../utils/auth-token'
import { getRoleSessionTimeout } from '../services/rbac-phi.service'

export interface AuthContextValue {
  user: User | null
  isAuthenticated: boolean
  isLoading: boolean
  requiresOtp: boolean
  otpEmail: string | null
  login: (email: string, password: string) => Promise<string>
  confirmOtp: (code: string) => Promise<void>
  logout: () => Promise<void>
  signInWithGoogle: () => Promise<void>
  signUp: (data: any) => Promise<void>
  hasPermission: (permission: string) => boolean
  hasRole: (role: UserRole) => boolean
  error: string | null
  token: string | null
  refreshProfile: () => Promise<User | null>
  /** Call this during active recording/processing to prevent session timeout */
  keepAlive: () => void
}

export const AuthContext = createContext<AuthContextValue | undefined>(undefined)

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // 2FA State
  const [requiresOtp, setRequiresOtp] = useState(false)
  const [otpEmail, setOtpEmail] = useState<string | null>(null)

  const hasInitialized = useRef(false)

  /**
   * Fetch user profile from backend
   */
  const fetchUserProfile = useCallback(async (): Promise<User | null> => {
    try {
      const userProfile = await userService.getCurrentUser()
      const transformedUser = userService.transformProfileToUser(userProfile)

      setUser(transformedUser)
      sessionStorage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(transformedUser))
      return transformedUser
    } catch (err) {
      console.error('[AuthContext] Failed to fetch user profile:', err)
      return null
    }
  }, [])

  /**
   * Initialize Auth State
   */
  useEffect(() => {
    const initializeAuth = async () => {
      if (hasInitialized.current) return

      const token = getAccessToken()
      if (token) {
        const cachedUserData = sessionStorage.getItem(STORAGE_KEYS.USER_DATA)
        if (cachedUserData) {
          try {
            const parsedUser = JSON.parse(cachedUserData)
            setUser(parsedUser)

            // Keep startup fast, but refresh from the API when newer fields
            // like feature flags are missing from the cached shape.
            const needsProfileRefresh =
              !parsedUser?.featureFlags ||
              typeof parsedUser.featureFlags.enable_worker_streaming === 'undefined' ||
              !parsedUser?.recordingLimits ||
              typeof parsedUser.recordingLimits.sessionTimeLimitMinutes === 'undefined'

            if (!needsProfileRefresh) {
              setIsLoading(false)
              hasInitialized.current = true
              return
            }
          } catch (e) {
            console.error('Failed to parse cached user data')
          }
        }

        const fetchedUser = await fetchUserProfile()
        if (!fetchedUser) {
          clearAccessToken()
          setUser(null)
        }
      }

      setIsLoading(false)
      hasInitialized.current = true
    }

    initializeAuth()
  }, [fetchUserProfile])

  /**
   * Login Action
   */
  const login = useCallback(
    async (email: string, password: string): Promise<string> => {
      setIsLoading(true)
      setError(null)
      try {
        const response = await authService.login(email, password)

        if (response.action === 'otp_required') {
          setRequiresOtp(true)
          setOtpEmail(email)
          setIsLoading(false)
          return 'otp_required'
        }

        if (response.action === 'verify_email') {
          setOtpEmail(email)
          setIsLoading(false)
          return 'verify_email'
        }

        if (response.action === 'success') {
          await fetchUserProfile()
        }
        return 'success'
      } catch (err: any) {
        const detail = err.response?.data?.detail || err.message || 'Login failed'
        setError(detail)
        throw err
      } finally {
        setIsLoading(false)
      }
    },
    [fetchUserProfile]
  )

  /**
   * Confirm OTP Action
   */
  const confirmOtp = useCallback(
    async (code: string) => {
      if (!otpEmail) throw new Error('No email context for OTP')

      setIsLoading(true)
      try {
        const response = await authService.verifyOtp(otpEmail, code)
        if (response.action === 'success') {
          await fetchUserProfile()
          setRequiresOtp(false)
          setOtpEmail(null)
        }
      } catch (err: any) {
        setError(err.message || 'Invalid OTP')
        throw err
      } finally {
        setIsLoading(false)
      }
    },
    [otpEmail, fetchUserProfile]
  )

  /**
   * Logout Action
   */
  const logout = useCallback(async () => {
    await authService.logout()
    setUser(null)
    setRequiresOtp(false)
    setOtpEmail(null)
    sessionStorage.removeItem(STORAGE_KEYS.USER_DATA)
  }, [])

  /**
   * Google Sign In
   */
  const signInWithGoogle = useCallback(async () => {
    await authService.initiateGoogleSignIn()
  }, [])

  /**
   * Sign Up
   */
  const signUp = useCallback(async (data: any) => {
    setIsLoading(true)
    try {
      const { email, password, firstName, lastName, specialty } = data
      await authService.register(email, password, firstName || '', lastName || '', specialty || '')
    } catch (err: any) {
      setError(err.message || 'Registration failed')
      throw err
    } finally {
      setIsLoading(false)
    }
  }, [])

  // RBAC Helpers
  const hasPermission = useCallback((permission: string) => user?.permissions?.includes(permission) ?? false, [user])
  const hasRole = useCallback((role: UserRole) => user?.role === role, [user])

  // Expose resetTimer so keepAlive can call it from outside the effect
  const resetTimerRef = useRef<(() => void) | null>(null)

  // Session Timeout Logic
  useEffect(() => {
    if (!user) return
    const sessionTimeout = getRoleSessionTimeout(user.role as UserRole)
    if (!sessionTimeout) return

    const timeoutMs = sessionTimeout * 60 * 1000
    let timer: any

    const resetTimer = () => {
      clearTimeout(timer)
      timer = setTimeout(() => {
        console.warn('Session expired')
        logout()
        window.location.href = '/login?reason=timeout'
      }, timeoutMs)
    }

    resetTimerRef.current = resetTimer

    const events = ['mousedown', 'mousemove', 'click', 'keydown', 'scroll', 'touchstart', 'touchmove', 'visibilitychange']
    events.forEach((e) => window.addEventListener(e, resetTimer, { passive: true }))
    resetTimer()

    return () => {
      resetTimerRef.current = null
      clearTimeout(timer)
      events.forEach((e) => window.removeEventListener(e, resetTimer))
    }
  }, [user, logout])

  const keepAlive = useCallback(() => {
    resetTimerRef.current?.()
  }, [])

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        isLoading,
        requiresOtp,
        otpEmail,
        login,
        confirmOtp,
        logout,
        signInWithGoogle,
        signUp,
        hasPermission,
        hasRole,
        error,
        token: getAccessToken(),
        refreshProfile: fetchUserProfile,
        keepAlive,
      }}>
      {children}
    </AuthContext.Provider>
  )
}
